import type { ContinuousScrollerRef } from '../../continuous-scroller/continuous-scroller.component';
import type { JsonData } from '../../generic-types';
import type { DataGridColumnSettings } from '../data-grid-settings.interface';
import type { Signal } from '@preact/signals-react';

export class Data {
  private _scrollerRef?: ContinuousScrollerRef;
  private _staticDataContainer?: HTMLDivElement;
  private resetViewToInitialState: () => void;
  private _columns: Signal<Array<DataGridColumnSettings>>;
  private _staticData: Signal<JsonData>;
  private _selectedRows: Signal<JsonData>;
  private _highlightedRows: Signal<JsonData>;

  constructor(
    resetViewToInitialState: () => void,
    _columns: Signal<Array<DataGridColumnSettings>>,
    _staticData: Signal<JsonData>,
    _selectedRows: Signal<JsonData>,
    _highlightedRows: Signal<JsonData>
  ) {
    this.resetViewToInitialState = resetViewToInitialState;
    this._columns = _columns;
    this._staticData = _staticData;
    this._selectedRows = _selectedRows;
    this._highlightedRows = _highlightedRows;
  }

  get selectedRows() {
    return this._selectedRows.value;
  }

  get highlightedRows() {
    return this._highlightedRows.value;
  }

  get columns() {
    return this._columns.value;
  }

  get staticData() {
    return this._staticData.value;
  }

  set scrollerRef(value: ContinuousScrollerRef) {
    this._scrollerRef = value;
  }

  set staticDataContainer(value: HTMLDivElement) {
    this._staticDataContainer = value;
  }

  /**
   * Removes a row by its ID field.
   * @param {number | string} id - The id value of the row to remove.
   */
  removeRowById(id: number | string) {
    this._scrollerRef?.removeItem(id);
  }

  /**
   * Gets all the currently loaded rows in the data grid.
   * @returns {JsonData}
   */
  getAllRows() {
    return this._scrollerRef?.getAllData() || [];
  }

  /**
   * Returns the data container client width
   */
  getClientWidth() {
    return this._scrollerRef?.getClientWidth() ?? this._staticDataContainer?.clientWidth;
  }

  /**
   * Makes a call through the getData function and clears the current page data.
   */
  getDataOverwritePageCache() {
    this._scrollerRef?.getDataOverwritePageCache();
  }

  /**
   * Tells the grid that a record has been changed from outside the grid.
   * @param {number | string} recordId - The record id that was changed.
   */
  recordChanged(recordId: number | string) {
    this._scrollerRef?.recordChanged(recordId);
  }

  /**
   * Adds one or more rows to the grids selected rows.
   * @param {JsonData} rows - The data rows to set selected.
   * @param {boolean} replaceCurrentSelection - If set to true the current selections will be replaced instead of added to.
   */
  addSelectedRows(rows: JsonData, replaceCurrentSelection = false) {
    if (rows.length > 0) {
      if (replaceCurrentSelection) {
        this._selectedRows.value = rows;
      } else {
        this._selectedRows.value = [...this._selectedRows.value, ...rows];
      }
    }
  }

  /**
   * Removes all selected rows from the grid.
   */
  removeSelectedRows() {
    this._selectedRows.value = [];
  }

  /**
   * Adds one or more rows to the grids highlighted rows.
   * @param {JsonData} rows - The data rows to set highlighted.
   * @param {boolean} replaceCurrentSelection - If set to true the current selections will be replaced instead of added to.
   */
  addHighlightedRows(rows: JsonData, replaceCurrentSelection = false) {
    if (rows.length > 0) {
      if (replaceCurrentSelection) {
        this._highlightedRows.value = rows;
      } else {
        this._highlightedRows.value = [...(this._highlightedRows.value || []), ...rows];
      }
    }
  }

  /**
   * Removes all highlighted rows from the grid.
   */
  removeHighlightedRows() {
    this._highlightedRows.value = [];
  }

  resetView() {
    this.resetViewToInitialState();
  }

  setColumns(columns: Array<DataGridColumnSettings>) {
    this._columns.value = columns;
  }

  setResetView(resetView: () => void) {
    this.resetViewToInitialState = resetView;
  }
}
